import { Box, Button } from '@mui/material'
import { LocationDisplay } from 'Components/Cards/LocationCard/LocationDisplay'
import { LocationDetails } from 'Components/Cards/LocationCard/LocationDetails'
import { type location } from 'core/types'
import { Modal } from 'Components/Modal'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MapStateName } from 'Components/Cards/LocationCard/mapStateName'

export interface LocationCardDisplayProps {
  errors?: string[]
  childID: number | null
  isPrimary: boolean
  data: location
  edit: boolean
  anchorEl: HTMLButtonElement | null
  onEditChange?: (e: any) => void
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
  onUpdate: (editData: location) => void
  onCancel: () => void
  onPlaceSelected?: (place: Microsoft.Maps.ISuggestionResult) => Promise<void>
  showHomelessOption: boolean
  readOnly?: boolean
}

export function LocationCardDisplay (props: LocationCardDisplayProps): JSX.Element {
  const [deleteChecked, setDeleteChecked] = useState(false)
  const [editData, setEditData] = useState<location>(props.data)
  const [isdChanging, setIsdChanging] = useState<boolean | null>(null) // if null then the ISD is not changing, if false it's changing but not confirmed, if true it's changing and confirmed

  useEffect(() => {
    setDeleteChecked(false)
  }, [props.edit])

  const onUpdate = (): void => {
    if (isdChanging !== null && !isdChanging) {
      toast.error('Please confirm the ISD change before updating the address')
    } else if (deleteChecked) {
      props.onDelete()
    } else {
      props.onUpdate(editData)
    }
  }

  const onDeleteChecked = (checked: boolean): void => {
    setDeleteChecked(checked)
  }

  const onISDChanging = (confirmed: boolean): void => {
    setIsdChanging(confirmed)
  }

  const handleEditChange = (e: any): void => {
    setEditData({ ...editData, [e.target.name]: e.target.value })
  }

  const handlePlaceSelected = async (place: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    const newplace: location = { ...editData }
    newplace.address = place.address.addressLine
    newplace.city = place.address.locality
    newplace.apartment = ''
    newplace.state = MapStateName(place.address.adminDistrict)
    newplace.zipCode = place.address.postalCode
    // newplace.isPrimary = false
    setEditData(newplace)
  }

  const editContent = <LocationDetails
    editData={editData}
    childID={props.childID}
    onChange={handleEditChange}
    onDeleteChecked={onDeleteChecked}
    onISDChanging={onISDChanging}
    onPlaceSelected={handlePlaceSelected}
    errors={props.errors}
    hidePrimary={true}
    showHomelessOption={props.showHomelessOption}
  />

  return (<>
    <LocationDisplay location={props.data} isPrimary={props.isPrimary} isHomeless={props.data.isHomeless} />
    {props.readOnly !== true && (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button color='primary' variant='contained' onClick={props.onEdit} data-testid={'editAddress-' + props.data.id?.toString()} sx={{ textTransform: 'none', px: 4 }}>Edit Address</Button>
      </Box>
    )}

    {props.edit && <Modal
      titleContent={<div className='modalTitle'>Address Details</div>}
      confirmationContent={editContent}
      onClose={props.onCancel}
      onCancel={props.onCancel}
      open={true}
      onConfirm={onUpdate}
      maxWidth='sm'
      confirmButtonColor={deleteChecked ? 'error' : 'primary'}
      confirmButtonText={deleteChecked ? 'Delete Address' : 'Save Address'}
    />}
  </>
  )
}
